import { motion } from 'motion/react';
import { CSSProperties, ReactNode } from 'react';
import { twMerge } from 'tailwind-merge';

import { Icon } from '~/modules/elements/akui/icon';
import { formatSongLength } from '~/modules/songs/utils/format-song-length';
import { languageCode } from '~/modules/utils/language-name-to-iso-code';
import SongThumbnail from '~/routes/remote-mic/components/song-thumbnail';

export interface PhoneSong {
  id: string;
  title: string;
  artist: string;
  video?: string;
  language?: string | string[];
  duration?: number;
  tracksCount?: number;
}

type RowProps = { style?: CSSProperties } & Record<`data-${string}`, string | number>;

const ACTION =
  'flex shrink-0 cursor-pointer items-center justify-center border-none transition-transform duration-100 active:scale-90';

export function SongRow({
  song,
  saved,
  queuedAt,
  indented,
  canQueue,
  canSelect,
  owner,
  onStar,
  onQueue,
  onSelect,
  rowProps,
}: {
  song: PhoneSong;
  saved: boolean;
  // Place in the running order, counting from 1; 0 when it is not queued.
  queuedAt: number;
  indented: boolean;
  canQueue: boolean;
  canSelect: boolean;
  owner: string | null;
  onStar: () => void;
  onQueue: () => void;
  onSelect: () => void;
  rowProps?: RowProps;
}) {
  const listName = owner ? `${owner}'s list` : 'your list';
  const queued = queuedAt > 0;

  return (
    <div
      {...rowProps}
      data-test={song.id}
      data-saved={saved}
      className={twMerge(
        'grid h-19 grid-cols-[3.5rem_minmax(0,1fr)_auto] items-center gap-3 border-b border-white/6 pr-3',
        indented ? 'pl-10' : 'pl-4',
        saved ? 'bg-[rgba(255,165,0,0.08)]' : 'bg-transparent',
      )}>
      <SongThumbnail video={song.video} />
      <SongText song={song} />
      <span className="flex items-center gap-1">
        <button
          type="button"
          aria-label={saved ? `Remove from ${listName}` : `Save to ${listName}`}
          data-test={saved ? 'remove-song-button' : 'add-song-button'}
          onClick={(event) => {
            event.stopPropagation();
            onStar();
          }}
          className={twMerge(ACTION, 'h-11 w-10 rounded-xl bg-transparent')}>
          <motion.span
            key={String(saved)}
            initial={saved ? { scale: 1 } : false}
            animate={saved ? { scale: [1, 1.28, 1] } : { scale: 1 }}
            transition={{ duration: 0.4 }}
            className="flex">
            <Icon
              icon={saved ? 'ic:baseline-star' : 'ic:baseline-star-outline'}
              size={5.5}
              className={saved ? 'text-active' : 'text-white/70'}
            />
          </motion.span>
        </button>
        {canQueue && (
          <button
            type="button"
            aria-label={queued ? `In the queue at #${queuedAt}` : 'Add to the queue'}
            data-test="queue-song-button"
            data-queued={queued}
            onClick={(event) => {
              event.stopPropagation();
              onQueue();
            }}
            className={twMerge(
              ACTION,
              'size-10 rounded-xl',
              queued
                ? 'text-active bg-[rgba(255,165,0,0.16)] shadow-[inset_0_0_0_1.5px_rgba(255,165,0,0.7)]'
                : 'bg-black/55 text-white shadow-[inset_0_0_0_1px_rgba(255,255,255,0.14)]',
            )}>
            <Icon icon={queued ? 'ic:baseline-playlist-add-check' : 'ic:baseline-playlist-add'} size={5.5} />
          </button>
        )}
        {canSelect && (
          <button
            type="button"
            aria-label="Sing this now"
            data-test="select-song-button"
            onClick={(event) => {
              event.stopPropagation();
              onSelect();
            }}
            className={twMerge(ACTION, 'bg-active size-10 rounded-full text-black')}>
            <Icon icon="ic:baseline-play-arrow" size={6} />
          </button>
        )}
      </span>
    </div>
  );
}

function SongText({ song, extra }: { song: PhoneSong; extra?: ReactNode }) {
  const language = Array.isArray(song.language) ? song.language[0] : song.language;
  const code = language ? languageCode(language) : undefined;
  const length = formatSongLength(song.duration);
  const duet = (song.tracksCount ?? 1) > 1;

  return (
    <span className="flex min-w-0 flex-col gap-[0.1875rem]">
      <span className="truncate text-[1rem] font-bold">{song.title}</span>
      <span className="truncate text-[0.8125rem] text-white/72">{song.artist}</span>
      {extra ?? (
        <span className="flex gap-2 text-[0.75rem] text-white/60">
          {code && (
            <span data-test="song-language" className="font-bold tracking-[0.06em] text-white/80 uppercase">
              {code}
            </span>
          )}
          {length && <span>{length}</span>}
          {duet && <span>· Duet</span>}
        </span>
      )}
    </span>
  );
}

export function ArtistGroupRow({
  artist,
  count,
  expanded,
  onToggle,
  rowProps,
}: {
  artist: string;
  count: number;
  expanded: boolean;
  onToggle: () => void;
  rowProps?: RowProps;
}) {
  return (
    <div
      {...rowProps}
      role="button"
      onClick={(event) => {
        event.stopPropagation();
        onToggle();
      }}
      data-test={`song-group-${artist}`}
      data-song-count={count}
      className="grid h-19 cursor-pointer grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-3.5 border-b border-white/8 bg-black/60 px-5 transition-colors duration-200 active:bg-black">
      <Icon
        icon="ic:baseline-arrow-right"
        size={5.5}
        className={`shrink-0 text-white transition-transform duration-200 ${expanded ? 'rotate-90' : ''}`}
      />
      <span className="flex min-w-0 flex-col gap-[0.1875rem]">
        <span className="truncate text-[1.0625rem] font-bold">{artist}</span>
        <span className="text-[0.875rem] text-white/60">{count} songs</span>
      </span>
      <span className="shrink-0 text-[0.8125rem] font-bold tracking-[0.06em] text-white/78">
        {expanded ? 'CLOSE' : 'EXPAND'}
      </span>
    </div>
  );
}

export function QueueRow({
  song,
  position,
  addedBy,
  mine,
  canQueue,
  onMoveUp,
  onRemove,
}: {
  song: PhoneSong;
  position: number;
  addedBy?: string;
  mine: boolean;
  canQueue: boolean;
  onMoveUp: () => void;
  onRemove: () => void;
}) {
  const first = position === 1;

  return (
    <div
      data-test={`queue-entry-${song.id}`}
      data-position={position}
      className={twMerge(
        'grid min-h-19 grid-cols-[1.25rem_3.5rem_minmax(0,1fr)_auto] items-center gap-3 border-b border-white/6 pr-3 pl-4',
        mine ? 'bg-[rgba(255,165,0,0.06)]' : 'bg-transparent',
      )}>
      <span className="text-center text-[0.9375rem] font-bold text-white/70">{position}</span>
      <SongThumbnail video={song.video} />
      <SongText
        song={song}
        extra={
          <span className={twMerge('ph-no-capture truncate text-[0.75rem]', mine ? 'text-active' : 'text-white/60')}>
            {mine ? 'Added by you' : addedBy ? `Added by ${addedBy}` : 'Added on the TV'}
          </span>
        }
      />
      {canQueue && (
        <span className="flex gap-1">
          <button
            type="button"
            aria-label="Move up"
            disabled={first}
            onClick={onMoveUp}
            data-test="queue-move-up"
            className={twMerge(ACTION, 'size-10 rounded-xl bg-transparent text-white disabled:opacity-30')}>
            <Icon icon="ic:baseline-arrow-upward" size={5} />
          </button>
          <button
            type="button"
            aria-label="Remove from the queue"
            onClick={onRemove}
            data-test="queue-remove"
            className={twMerge(
              ACTION,
              'size-10 rounded-xl bg-black/55 text-white shadow-[inset_0_0_0_1px_rgba(255,255,255,0.14)]',
            )}>
            <Icon icon="ic:baseline-close" size={5} />
          </button>
        </span>
      )}
    </div>
  );
}
